<script setup lang="ts">
import { computed, ref } from 'vue'

import { SeriesType } from '@xcel-next/core/enums'

import type { TeaserParagraph } from '../../utils/paragraph-schema/paragraphs'
import type { ComposedPageMeta } from '@xcel-next/composer-schema'


const props = defineProps<{
  series: TeaserParagraph['teaser'][],
  pageMeta: ComposedPageMeta,
  isPreview?: boolean
}>()

const seriesToc = ref<HTMLElement | null>(null)
const seriesTocOpen = ref(false)

const linkLeft = computed(() => {
  const index = props.pageMeta.seriesIndex ?? 0
  return index === 0 ? props.series[props.series.length - 1]?.url : props.series[index - 1]?.url
})

const linkRight = computed(() => {
  const index = props.pageMeta.seriesIndex ?? 0
  return index + 1 < props.series.length ? props.series[index + 1]?.url : props.series[0]?.url
})

const styleClasses = computed(() => ({
  'bx-content': true,
  'bx-series': true,
  'bx-series--table-of-contents': true,
  'bx-typo--series': true,
  'bx-js-toc-hide': !props.pageMeta.unfoldToc
}))

const ariaLabel = computed(() => seriesTocOpen.value ? 'Artikelübersicht schliessen' : 'Artikelübersicht öffnen')

function toggleSeriesToc () {
  seriesTocOpen.value = !seriesToc.value?.classList.toggle('bx-js-toc-hide')
}

function setUrl (url?: string): string {
  return props.isPreview ? '#' : (url ?? '#')
}
</script>

<template>
  <div v-if="pageMeta.seriesType && pageMeta.seriesType === SeriesType.browse"
       ref="seriesToc"
       :class="styleClasses"
       tabindex="0"
       role="button"
       :aria-label="ariaLabel"
       aria-controls="bx-aria-series-toc"
       :aria-expanded="seriesTocOpen"
       @click="toggleSeriesToc"
       @keyup.enter="toggleSeriesToc">
    <div class="bx-series__toc-headline">
      <span class="bx-series__toc-headline-text">Artikelübersicht</span>
      <svg class="icon icon-arrow_toggle">
        <use href="#arrow_toggle" />
      </svg>
    </div>
    <ul v-if="series"
        id="bx-aria-series-toc"
        class="bx-series__toc">
      <li v-for="(item, index) in series"
          :key="index">
        <a v-if="item.assetId !== pageMeta.id"
           :href="isPreview ? '#' : item.url">
          {{ item.assetDisplayTitle || item.titleLong || item.titleShort }}
        </a>
        <span v-else>
          {{ item.assetDisplayTitle || item.titleLong || item.titleShort }}
        </span>
      </li>
    </ul>
  </div>

  <div v-else-if="pageMeta.seriesType && pageMeta.seriesType === SeriesType.slide"
       class="bx-content bx-series bx-series--slide-navi bx-typo--series">
    <a :href="setUrl(linkLeft)">
      <div class="bx-form__button"
           aria-label="Vorheriger Artikel">
        <svg class="icon icon-arrow_series-left"
             aria-hidden="true">
          <use href="#arrow_series-left" />
        </svg>
      </div>
    </a>

    <span class="bx-series__page-info">
      {{ (pageMeta.seriesIndex ?? 0) + 1 }} / {{ series.length }}
    </span>

    <a :href="setUrl(linkRight)">
      <div class="bx-form__button"
           aria-label="Nächster Artikel">
        <svg class="icon icon-arrow_series-right"
             aria-hidden="true">
          <use href="#arrow_series-right" />
        </svg>
      </div>
    </a>
  </div>
</template>
